import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import ORTypography from 'components/ORTypography/ORTypography';
import MorePropTypes from 'airbnb-prop-types';
import MenuItem from '@material-ui/core/MenuItem';
import TableCell from '@material-ui/core/TableCell';
import RBACDeleteButton from 'components/RBACWrapper/RBACDeleteButton/RBACDeleteButton';
import TextField from '@material-ui/core/TextField';

const propTypes = {
  keyName: PropTypes.string.isRequired,
  addConfig: PropTypes.func.isRequired,
  deleteConfig: PropTypes.func.isRequired,
  catalogItem: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    restartRequired: PropTypes.bool.isRequired,
  }),
  classes: PropTypes.shape({}).isRequired,
  error: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.string, PropTypes.shape({})]),
  keyValue: MorePropTypes.or([PropTypes.string, PropTypes.number]).isRequired,
};

const defaultProps = {
  error: null,
  catalogItem: null,
};

export function UnstyledSettingsRowItem({
  classes,
  keyName,
  keyValue,
  error,
  catalogItem,
  addConfig,
  deleteConfig,
}) {
  const errorElm = error ? (
    <ORTypography color="error" className={classes.formStatus}>
      {error}
    </ORTypography>
  ) : null;

  const restartStarTitle = (restart) =>
    restart ? (
      <ORTypography className={classes.configKeyText}>
        {catalogItem.name}
        &nbsp;
        <span style={{ color: 'red' }}>*</span>
      </ORTypography>
    ) : (
      <ORTypography className={classes.configKeyText}>{catalogItem.name}</ORTypography>
    );

  const fieldDataType = (cfg) => {
    switch (cfg.value.type) {
      case 'integer':
        return (
          <TextField
            name={keyName}
            type="number"
            variant="outlined"
            className={classes.field}
            fullWidth
            value={keyValue}
            onChange={(e) => addConfig(keyName, parseInt(e.target.value, 10))}
          />
        );
      default:
        // string or enum
        if ('enum' in cfg.value) {
          return (
            <TextField
              name={keyName}
              variant="outlined"
              className={classes.field}
              select
              fullWidth
              value={keyValue || cfg.value.enum[0]}
              onChange={(e) => addConfig(keyName, e.target.value)}
            >
              {cfg.value.enum.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          );
        }
        return (
          <TextField
            name={keyName}
            variant="outlined"
            className={classes.field}
            fullWidth
            value={keyValue}
            onChange={(e) => addConfig(keyName, e.target.value)}
          />
        );
    }
  };

  if (!catalogItem) {
    return (
      <Card elevation={0} className={classes.card}>
        {keyName}
        &nbsp; IS NOT PROPERLY SEEDED IN THE CONFIGURATION CATALOG
      </Card>
    );
  }

  const restart = catalogItem.restartRequired;

  return (
    <React.Fragment>
      <div className={classes.row}>
        <TableCell classes={{ root: classes.cellLeft }} flex="auto">
          {restartStarTitle(restart)}
        </TableCell>
        <TableCell classes={{ root: classes.cellMiddle }} flex="auto">
          <div className={classes.nameField}>
            {fieldDataType(catalogItem)}
            {errorElm}
          </div>
        </TableCell>
        <TableCell classes={{ root: classes.cellRight }}>
          <RBACDeleteButton
            size="medium"
            classes={{ listItemDeleteRoot: classes.makeSquared }}
            onClick={() => deleteConfig(keyName)}
          />
        </TableCell>
      </div>
    </React.Fragment>
  );
}

UnstyledSettingsRowItem.propTypes = propTypes;
UnstyledSettingsRowItem.defaultProps = defaultProps;

const styles = (theme) => ({
  field: {
    backgroundColor: 'white',
  },
  clearButton: {
    color: theme.customColors.red,
    backgroundColor: 'inherit',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'inherit',
    },
  },
  configKeyText: {
    fontFamily: theme.typography.altTitle.fontFamily,
    fontWeight: theme.typography.altTitle.fontWeight,
    fontSize: '16px',
    color: '#231F20',
  },
  cellLeft: {
    display: 'flex',
    alignItems: 'center',
    flex: '40%',
    paddingRight: '0px',
  },
  cellMiddle: {
    flex: '55%',
    paddingRight: '0px',
  },
  cellRight: {
    flex: '5%',
    display: 'flex',
    alignItems: 'center',
    margin: 'auto',
    paddingRight: '8px',
    paddingLeft: '6px',
  },
  row: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  makeSquared: {
    borderRadius: '4px',
  },
});

export default withStyles(styles)(UnstyledSettingsRowItem);
